import { supabase } from '@/integrations/supabase/client';
import { format } from 'date-fns';
import type {
  AttendanceSettings,
  AttendanceFlag,
  AttendanceFlagFilters,
  UpdateAttendanceSettingsInput,
  ResolveAttendanceFlagInput,
} from './types';

export const attendanceService = {
  // Get attendance settings for company (returns null if not found)
  async getAttendanceSettings(companyId: string): Promise<AttendanceSettings | null> {
    const { data, error } = await supabase
      .from('attendance_settings')
      .select('*')
      .eq('company_id', companyId)
      .maybeSingle(); // Returns null instead of throwing for missing row

    if (error) throw error;
    return data;
  },

  // Update attendance settings (uses update-or-insert pattern to handle missing rows)
  async updateAttendanceSettings(
    companyId: string,
    settings: UpdateAttendanceSettingsInput
  ): Promise<AttendanceSettings> {
    // First, try to update existing row
    const { data: updateResult, error: updateError } = await supabase
      .from('attendance_settings')
      .update(settings)
      .eq('company_id', companyId)
      .select();

    if (updateError) throw updateError;

    // If update affected a row, return it
    if (updateResult && updateResult.length > 0) {
      return updateResult[0];
    }

    // No existing row - insert new one with provided settings
    const { data: insertResult, error: insertError } = await supabase
      .from('attendance_settings')
      .insert({ company_id: companyId, ...settings })
      .select()
      .single();

    if (insertError) throw insertError;
    return insertResult;
  },

  // Get attendance flags with filters and pagination
  async getAttendanceFlags(
    companyId: string,
    filters: AttendanceFlagFilters = {},
    pagination: { limit?: number; offset?: number } = {}
  ): Promise<{ data: AttendanceFlag[]; totalCount: number; hasMore: boolean }> {
    const limit = pagination.limit ?? 50;
    const offset = pagination.offset ?? 0;

    // Single query with JOIN - fetches flags AND profiles together
    let query = supabase
      .from('attendance_event_flags')
      .select(`
        *,
        profile:profiles!attendance_event_flags_profile_id_fkey(
          first_name,
          last_name,
          email
        )
      `, { count: 'exact' })
      .eq('company_id', companyId)
      .order('event_date', { ascending: false })
      .order('created_at', { ascending: false });

    if (filters.profileId) {
      query = query.eq('profile_id', filters.profileId);
    }

    if (filters.startDate) {
      query = query.gte('event_date', format(filters.startDate, 'yyyy-MM-dd'));
    }

    if (filters.endDate) {
      query = query.lte('event_date', format(filters.endDate, 'yyyy-MM-dd'));
    }

    if (filters.flagType) {
      query = query.eq('flag_type', filters.flagType);
    }

    if (filters.flagSeverity) {
      query = query.eq('flag_severity', filters.flagSeverity);
    }

    if (filters.resolved !== undefined) {
      query = query.eq('auto_resolved', filters.resolved);
    }

    // Apply pagination
    query = query.range(offset, offset + limit - 1);

    const { data, error, count } = await query;

    if (error) throw error;

    const totalCount = count ?? 0;
    return {
      data: (data || []) as AttendanceFlag[],
      totalCount,
      hasMore: offset + limit < totalCount,
    };
  },

  // Resolve an attendance flag
  async resolveFlag(input: ResolveAttendanceFlagInput): Promise<void> {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) throw new Error('Not authenticated');

    const { data: profile } = await supabase
      .from('profiles')
      .select('id')
      .eq('user_id', user.id)
      .single();

    if (!profile) throw new Error('Profile not found');

    const { error } = await supabase
      .from('attendance_event_flags')
      .update({
        auto_resolved: true,
        resolved_at: new Date().toISOString(),
        resolved_by: profile.id,
        resolution_note: input.resolutionNote || null,
      })
      .eq('id', input.flagId);

    if (error) throw error;
  },

  // Trigger manual missed punch detection
  async triggerMissedPunchDetection(): Promise<{ success: boolean; message: string }> {
    const { data, error } = await supabase.functions.invoke('detect-missed-punches');

    if (error) throw error;
    return data;
  },
};